// components/common/filters/EmployeeSearchAutocomplete.tsx
import { Launch } from '@mui/icons-material';
import { Autocomplete, Box, TextField, Chip } from '@mui/material';
import _ from 'lodash';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDebouncedValue } from '../../../hooks/useDebounce';
import { ROUTE_PATHS } from '../../../routes/Routes';
import { useGetAllEmployees } from '../../../services/react-query/employees';
import { Employee } from '../../../types/Employee';
import { Loading } from '../../../uikit';
import { ErrorObject } from '../../../utility/inputUtils';

interface EmployeeSearchAutocompleteProps<Multiple extends boolean | undefined = false> {
  value: Multiple extends true ? Employee[] : Employee | null;
  onChange: (value: Multiple extends true ? Employee[] : Employee | null) => void;
  isMultiple?: Multiple;
  error?: ErrorObject;
  label?: string;
  excludedEmployees?: string[];
  isDisabled?: boolean;
}

export default function EmployeeSearchAutocomplete<Multiple extends boolean | undefined = false>({
  value,
  onChange,
  isMultiple,
  error,
  label,
  excludedEmployees = [],
  isDisabled = false,
}: EmployeeSearchAutocompleteProps<Multiple>) {
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebouncedValue(searchInput, 300);

  const { data: employeesData, isFetching } = useGetAllEmployees({
    params: { search: debouncedSearchInput },
    enabled: !!debouncedSearchInput,
  });

  const employees = _.reject(employeesData?.data ?? [], (emp) => _.includes(excludedEmployees, emp.uuid));

  return (
    <Box sx={{ display: 'inline-flex', gap: 1, width: '100%' }}>
      <Autocomplete<Employee, Multiple, false, false>
        fullWidth
        multiple={isMultiple}
        size="small"
        loading={isFetching}
        loadingText={<Loading size={22} />}
        options={employees}
        noOptionsText={debouncedSearchInput ? 'No employees found' : 'Search for employees'}
        disabled={isDisabled}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        getOptionLabel={(emp) => `${emp.display_name} – ${emp.email}`}
        renderInput={(params) => <TextField {...params} {...error} label={label || 'Employee'} size="small" onChange={(e) => setSearchInput(e.target.value)} />}
      />

      {!isMultiple && value && (
        <Link to={`/${ROUTE_PATHS.EMPLOYEES}/${(value as Employee).uuid}`} style={{ alignContent: 'center' }}>
          <Launch sx={{ color: 'black' }} />
        </Link>
      )}
    </Box>
  );
}
